import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, DefaultButton, Dropdown, Spinner, SpinnerSize } from '@fluentui/react';

import { AppState } from '../../../../redux/reducers';
import { Contract } from '../../../../models/Entities/Contracts/Contract';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { ProtocolConsumption } from '../../../../models/Entities/Protocols/ProtocolConsumption';
import { ProtocolPatient } from '../../../../models/Entities/Protocols/ProtocolPatient';
import { IConsumptionsFilters } from '../../../../models/Filters/ConsumptionsFilters';
import * as ConsumptionsService from '../../../../services/ProtocolConsumptions.Service';
import * as ProtocolsService from '../../../../services/MedicalProtocols.Services';

import ExcelExportButton from '../ExcelExportButton.Component';
import { buildFileName, downloadWorkbook } from '../StatisticsExcel.Functions';
import { buildContractVisitExecution } from '../VisitExecution.Functions';

import BillingFunnelComponent from './BillingFunnel.Component';
import BillingMatrixComponent from './BillingMatrix.Component';
import BillingLeaksComponent from './BillingLeaks.Component';
import SyncHealthComponent from './SyncHealth.Component';
import { billingControlSheets } from './BillingControl.Excel';
import AgreedVsBilledComponent from './AgreedVsBilled.Component';
import { agreedTotals, buildAgreedByStage, buildAgreedVsBilled } from './AgreedVsBilled.Functions';
import * as ContractStatisticsService from '../../../../services/Contract-Statistics.Services';
import {
    AddendumComparisonDto,
    ConceptTotalsDto,
    PatientTotalsDto,
    StatisticsSummaryDto
} from '../../../../models/Entities/Contracts/Statistics/ContractStatisticsModels';
import { buildFunnel, buildLeaks, buildMatrix, filterByPeriod } from './BillingControl.Functions';

import '../ContractStatistics.Styles.css';
import './BillingControl.Styles.css';

interface IProps {
    ContractId: number;
    /** Adenda elegida en la página del contrato; se puede cambiar desde el panel. */
    SelectedAddendumId?: number;
}

/** Pacientes que se traen de lo pactado: alcanza para leer, y el resto se dice. */
const PATIENTS_PAGE_SIZE = 25;

/** Fecha en el formato que espera la API de estadísticas del contrato. */
const isoDay = (date?: Date): string | undefined => (date ? date.toISOString().slice(0, 10) : undefined);

/** Protocolos que se consultan a la vez: cada uno son dos pedidos. */
const PROTOCOL_BATCH = 3;

/** Una página grande alcanza: el control necesita todos los consumos, no una página. */
const CONSUMPTIONS_PAGE_SIZE = 10000;

/** El servicio de consumos pide el filtro completo de la grilla; acá no se filtra nada. */
const EMPTY_FILTERS: IConsumptionsFilters = {
    SelectedConsumptions: [],
    SelectedProtocolPatients: [],
    SelectedContractConcepts: [],
    SelectedCurrencys: '',
    SelectedContractStages: [],
    SelectedPractitioners: [],
    SelectedStatus: [],
    SelectedProtocols: [],
    SelectedSettlements: [],
    SelectedDateRange: [undefined, undefined],
    FilterText: undefined,
    showGroupedByVisit: false,
    GroupId: undefined
};

interface ProtocolData {
    protocolId: number;
    label: string;
    consumptions: ProtocolConsumption[];
    patients: ProtocolPatient[];
}

/**
 * Control de facturación del contrato.
 *
 * Contesta dos preguntas: qué se cobra en qué visita, y qué puede estar quedando
 * afuera de la liquidación. Mira el contrato completo, juntando los consumos y los
 * pacientes de todos los protocolos asociados.
 *
 * Los cálculos son los mismos que se prueban en BillingControl.Functions: acá sólo
 * se juntan los datos de varios protocolos y se dibuja.
 */
export const ContractBillingControlComponent: React.FC<IProps> = ({ ContractId, SelectedAddendumId }) => {
    const AccessToken = useSelector((state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined);
    const contract = useSelector((state: AppState) =>
        state.Contract?.list?.find((item: Contract) => item.id === ContractId)
    );
    const protocolsInStore = useSelector((state: AppState) => state.MedicalProtocols?.list ?? []);

    const [data, setData] = useState<ProtocolData[]>([]);
    const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
    const [failures, setFailures] = useState<number[]>([]);

    const [periodFrom, setPeriodFrom] = useState<Date | undefined>(undefined);
    const [periodTo, setPeriodTo] = useState<Date | undefined>(undefined);
    const [addendumId, setAddendumId] = useState<number | undefined>(SelectedAddendumId);
    const hasPeriod = !!periodFrom || !!periodTo;

    // ── Lo pactado, desde la API de estadísticas del contrato ──
    const [summary, setSummary] = useState<StatisticsSummaryDto | null>(null);
    const [conceptTotals, setConceptTotals] = useState<ConceptTotalsDto[]>([]);
    const [agreedPatients, setAgreedPatients] = useState<PatientTotalsDto[]>([]);
    const [agreedPatientsTotal, setAgreedPatientsTotal] = useState<number | undefined>(undefined);
    const [addendumComparisons, setAddendumComparisons] = useState<AddendumComparisonDto[]>([]);
    const [isLoadingAgreed, setIsLoadingAgreed] = useState<boolean>(false);
    const [agreedFailed, setAgreedFailed] = useState<boolean>(false);

    const associations = useMemo(() => contract?.protocol ?? [], [contract]);
    const associationKey = associations.map((item) => item.protocolId).join(',');

    const labelOf = useMemo(
        () => (protocolId: number): string => {
            const protocol = protocolsInStore.find((item: Protocol) => item.id === protocolId);
            return protocol?.protocolNumber || protocol?.title || `#${protocolId}`;
        },
        [protocolsInStore]
    );

    /**
     * Consumos y pacientes de cada protocolo asociado, en tandas. Un protocolo que
     * falla no frena a los demás: se anota y se avisa, porque un control armado sobre
     * datos incompletos sin decirlo es peor que no tenerlo.
     */
    useEffect(() => {
        if (!AccessToken || associations.length === 0) {
            setData([]);
            setProgress({ done: 0, total: 0 });
            return;
        }

        let cancelled = false;
        const ids = associations.map((item) => item.protocolId);
        setData([]);
        setFailures([]);
        setProgress({ done: 0, total: ids.length });

        const run = async () => {
            const collected: ProtocolData[] = [];
            const failed: number[] = [];

            for (let index = 0; index < ids.length; index += PROTOCOL_BATCH) {
                const batch = ids.slice(index, index + PROTOCOL_BATCH);
                const results = await Promise.all(
                    batch.map(async (protocolId) => {
                        try {
                            const [consumptions, patients] = await Promise.all([
                                ConsumptionsService.GetAllProtocolConsumptionByProtocol(
                                    protocolId,
                                    EMPTY_FILTERS,
                                    1,
                                    CONSUMPTIONS_PAGE_SIZE,
                                    AccessToken
                                ),
                                ProtocolsService.GetProtocolPatients(protocolId, null, AccessToken)
                            ]);
                            return {
                                protocolId,
                                label: labelOf(protocolId),
                                consumptions: consumptions?.data ?? [],
                                patients: patients ?? []
                            } as ProtocolData;
                        } catch {
                            failed.push(protocolId);
                            return undefined;
                        }
                    })
                );

                if (cancelled) return;
                results.forEach((result) => {
                    if (result) collected.push(result);
                });
                setData([...collected]);
                setFailures([...failed]);
                setProgress({ done: Math.min(index + batch.length, ids.length), total: ids.length });
            }
        };

        void run();

        return () => {
            cancelled = true;
        };
        // associationKey resume las asociaciones; labelOf sólo afecta el texto.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [AccessToken, associationKey]);

    /**
     * Con varios protocolos, el número de paciente se repite entre ellos (el "001" de
     * uno no es el "001" del otro). Se les antepone el protocolo para que los cruces
     * por paciente no mezclen gente de estudios distintos.
     */
    const { consumptions, patients } = useMemo(() => {
        const multiple = data.length > 1;
        const prefix = (value: string | undefined, label: string) =>
            multiple ? `${label} · ${value ?? ''}`.trim() : value ?? '';

        return {
            consumptions: data.flatMap((item) =>
                item.consumptions.map((consumption) => ({
                    ...consumption,
                    patientNumberInProtocol: prefix(consumption.patientNumberInProtocol, item.label)
                }))
            ),
            patients: data.flatMap((item) =>
                item.patients.map((patient) => ({
                    ...patient,
                    numberInProtocol: prefix(patient.numberInProtocol, item.label)
                }))
            )
        };
    }, [data]);

    const periodConsumptions = useMemo(
        () => filterByPeriod(consumptions, periodFrom, periodTo),
        [consumptions, periodFrom, periodTo]
    );

    const addendums = useMemo(() => contract?.addendums ?? [], [contract]);

    /**
     * Lo pactado sale de otra API —la de estadísticas del contrato— y respeta el
     * mismo filtro de período y de adenda que el resto del panel, para que las dos
     * mitades hablen siempre de lo mismo.
     */
    useEffect(() => {
        if (!AccessToken || !ContractId) return;
        let cancelled = false;
        setIsLoadingAgreed(true);
        setAgreedFailed(false);

        const filter = { addendumId, from: isoDay(periodFrom), to: isoDay(periodTo) };
        const addendumIds = addendumId ? [addendumId] : addendums.map((item) => item.id);

        Promise.allSettled([
            ContractStatisticsService.GetSummary(ContractId, AccessToken),
            ContractStatisticsService.GetTotalsByConceptDetailed(ContractId, AccessToken, filter),
            ContractStatisticsService.GetTotalsByPatient(ContractId, AccessToken, {
                ...filter,
                page: 1,
                pageSize: PATIENTS_PAGE_SIZE
            }),
            addendumIds.length > 0
                ? ContractStatisticsService.GetCompareByAddendum(ContractId, AccessToken, addendumIds)
                : Promise.resolve([] as AddendumComparisonDto[])
        ])
            .then(([summaryResult, conceptResult, patientResult, compareResult]) => {
                if (cancelled) return;

                setSummary(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
                setConceptTotals(conceptResult.status === 'fulfilled' ? conceptResult.value ?? [] : []);
                setAgreedPatients(patientResult.status === 'fulfilled' ? patientResult.value?.data ?? [] : []);
                setAgreedPatientsTotal(patientResult.status === 'fulfilled' ? patientResult.value?.totalRecords : undefined);
                setAddendumComparisons(compareResult.status === 'fulfilled' ? compareResult.value ?? [] : []);

                // Sólo se avisa si falló todo: que falte la comparación por adenda no
                // invalida la comparación por concepto.
                setAgreedFailed([summaryResult, conceptResult, patientResult].every((item) => item.status === 'rejected'));
            })
            .finally(() => {
                if (!cancelled) setIsLoadingAgreed(false);
            });

        return () => {
            cancelled = true;
        };
    }, [AccessToken, ContractId, addendumId, periodFrom, periodTo, addendums]);

    const stages = contract?.stages ?? [];
    const concepts = contract?.concepts ?? [];
    const currency = periodConsumptions[0]?.currencyCode ?? contract?.contractCurrency ?? 'ARS';

    const visitModel = useMemo(
        () => buildContractVisitExecution(stages, patients, periodConsumptions),
        [stages, patients, periodConsumptions]
    );
    const funnel = useMemo(
        () => buildFunnel(concepts, stages, periodConsumptions, visitModel),
        [concepts, stages, periodConsumptions, visitModel]
    );
    const matrix = useMemo(
        () => buildMatrix(concepts, stages, periodConsumptions),
        [concepts, stages, periodConsumptions]
    );
    const leaks = useMemo(
        () => buildLeaks(concepts, periodConsumptions, visitModel),
        [concepts, periodConsumptions, visitModel]
    );

    const agreedRows = useMemo(() => buildAgreedVsBilled(conceptTotals, matrix), [conceptTotals, matrix]);
    const agreedStageRows = useMemo(() => buildAgreedByStage(conceptTotals, matrix), [conceptTotals, matrix]);
    const agreedSummary = useMemo(() => agreedTotals(agreedRows), [agreedRows]);

    const isLoading = progress.total > 0 && progress.done < progress.total;

    if (associations.length === 0) {
        return (
            <div className="cs-card">
                <div className="cs-card__header">
                    <div className="cs-card__title">Control de facturación</div>
                </div>
                <div className="cs-empty cs-empty--compact">
                    <span>Este contrato no tiene protocolos asociados.</span>
                    <span className="cs-empty__hint">
                        Sin protocolo no hay consumos que controlar: el control de facturación aparece cuando el
                        contrato se asocia a un protocolo.
                    </span>
                </div>
            </div>
        );
    }

    // El contenedor del sistema es el que separa las tarjetas entre sí; sin él, el
    // panel queda pegado dentro de la solapa del contrato.
    return (
        <div className="cs-container">
            <div className="cs-content">
            {/* ── Período y descarga ── */}
            <div className="cs-card">
                <div className="cs-card__header cs-card__header--actions">
                    <div className="cs-card__header-text">
                        <div className="cs-card__title">Control de facturación</div>
                        <div className="cs-card__desc">
                            Qué se cobra, dónde se cobra y qué puede estar quedando afuera de la liquidación. Toma los
                            consumos de {associations.length === 1 ? 'el protocolo asociado' : `los ${associations.length} protocolos asociados`} al
                            contrato. Por defecto mira todo el contrato; acotá el período para preparar una liquidación.
                        </div>
                    </div>
                    <ExcelExportButton
                        disabled={isLoading || periodConsumptions.length === 0}
                        onExport={() =>
                            downloadWorkbook(
                                buildFileName(`contrato-${contract?.contractNumber || ContractId}-control-de-facturacion`),
                                billingControlSheets(funnel, matrix, leaks),
                                {
                                    title: 'Control de facturación',
                                    scope: `Contrato ${contract?.contractNumber || ContractId}`,
                                    note: hasPeriod
                                        ? 'Período acotado: sólo incluye consumos con vencimiento en el rango elegido.'
                                        : 'Incluye todo el contrato, sin acotar por fechas.'
                                }
                            )
                        }
                    />
                </div>

                <div className="cs-card__toolbar">
                    {addendums.length > 1 && (
                        <Dropdown
                            styles={{ root: { minWidth: 200 } }}
                            placeholder="Todas las adendas"
                            options={[
                                { key: 0, text: 'Todas las adendas' },
                                ...addendums.map((item) => ({
                                    key: item.id,
                                    text: item.contractAddendumName || `Adenda ${item.id}`
                                }))
                            ]}
                            selectedKey={addendumId ?? 0}
                            onChange={(_event, option) => setAddendumId(option?.key ? Number(option.key) : undefined)}
                        />
                    )}
                    <DatePicker
                        styles={{ root: { minWidth: 180 } }}
                        placeholder="Desde (todo el contrato)"
                        ariaLabel="Desde"
                        value={periodFrom}
                        onSelectDate={(date) => setPeriodFrom(date ?? undefined)}
                    />
                    <DatePicker
                        styles={{ root: { minWidth: 180 } }}
                        placeholder="Hasta (todo el contrato)"
                        ariaLabel="Hasta"
                        value={periodTo}
                        onSelectDate={(date) => setPeriodTo(date ?? undefined)}
                    />
                    {hasPeriod && (
                        <DefaultButton
                            iconProps={{ iconName: 'Reset' }}
                            text="Todo el contrato"
                            onClick={() => {
                                setPeriodFrom(undefined);
                                setPeriodTo(undefined);
                            }}
                        />
                    )}
                    <span className="cs-kpi-cell__desc">
                        {hasPeriod
                            ? `${periodConsumptions.length} de ${consumptions.length} consumos en el período. Los que no tienen fecha de servicio se incluyen siempre.`
                            : `${consumptions.length} consumos en todo el contrato`}
                    </span>
                </div>

                {isLoading && (
                    <div className="cs-card__toolbar">
                        <div className="fp-load">
                            <Spinner size={SpinnerSize.xSmall} />
                            <span>
                                Trayendo consumos: {progress.done} de {progress.total} protocolos
                            </span>
                            <div className="cs-progress__bar fp-load__bar">
                                <div
                                    className="cs-progress__fill"
                                    style={{ width: `${progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {failures.length > 0 && (
                    <div className="cs-cf-error" role="alert">
                        <strong>
                            No se pudieron traer los consumos de {failures.length}{' '}
                            {failures.length === 1 ? 'protocolo' : 'protocolos'}.
                        </strong>{' '}
                        Lo que sigue está calculado sin ellos, así que puede faltar plata en los totales.
                    </div>
                )}

                {!isLoading && consumptions.length === 0 && failures.length === 0 && (
                    <div className="cs-empty cs-empty--compact">
                        <span>Todavía no hay consumos cargados en los protocolos de este contrato.</span>
                    </div>
                )}
            </div>

            {consumptions.length > 0 && (
                <>
                    <BillingFunnelComponent funnel={funnel} currency={currency} />
                    <BillingMatrixComponent matrix={matrix} currency={currency} />
                    <BillingLeaksComponent groups={leaks} currency={currency} />
                </>
            )}

            <AgreedVsBilledComponent
                summary={summary}
                rows={agreedRows}
                stageRows={agreedStageRows}
                totals={agreedSummary}
                patients={agreedPatients}
                patientsTotal={agreedPatientsTotal}
                addendums={addendumComparisons}
                currency={currency}
                isLoading={isLoadingAgreed && conceptTotals.length === 0}
                failed={agreedFailed}
            />

            {/* Una tarjeta por protocolo: la sincronización corre por protocolo, no por contrato. */}
            {data.map((item) => (
                <div key={item.protocolId}>
                    {data.length > 1 && (
                        <div className="cs-card__desc" style={{ margin: '16px 0 4px' }}>
                            Protocolo {item.label}
                        </div>
                    )}
                    <SyncHealthComponent ProtocolId={item.protocolId} AccessToken={AccessToken} />
                </div>
            ))}
            </div>
        </div>
    );
};

export default ContractBillingControlComponent;
